import { useNavigate, Link } from "react-router-dom";
import { ArrowRight, Clock, Target, Compass, ListChecks } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { LanguageToggle } from "@/components/LanguageToggle";
import { BrandLockup } from "@/components/BrandLockup";

const Wordmark = () => <BrandLockup nameClass="text-lg" tmClass="text-[10px]" />;

const ICONS = [Clock, Target, Compass, ListChecks];

export default function Landing() {
  const navigate = useNavigate();
  const { t } = useLang();

  return (
    <div className="min-h-screen bg-[#F6F5F2] flex flex-col">
      <header className="border-b border-[#E2DFD8] bg-[#F6F5F2]/95 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
          <Wordmark />
          <LanguageToggle />
        </div>
      </header>

      <main className="flex-1">
        <section className="max-w-4xl mx-auto px-6 md:px-12 pt-20 md:pt-32 pb-16">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#A68A56] mb-8">
            {t.landing.overline}
          </p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-[#0B0E14] leading-[1.05] max-w-3xl">
            {t.landing.title}
          </h1>
          <p className="text-base md:text-lg text-[#4A5568] leading-relaxed mt-8 max-w-2xl">
            {t.landing.subtitle}
          </p>

          <div className="mt-12">
            <button
              onClick={() => navigate("/diagnostic")}
              data-testid="find-my-bottleneck-btn"
              className="group inline-flex items-center gap-3 bg-[#0B0E14] text-white px-8 py-4 text-sm font-medium tracking-wide hover:bg-[#2C363F] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#A68A56] focus-visible:ring-offset-2 focus-visible:ring-offset-[#F6F5F2]"
            >
              {t.landing.cta}
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" strokeWidth={1.75} />
            </button>
          </div>
        </section>

        <section className="max-w-4xl mx-auto px-6 md:px-12 pb-28">
          <div className="border-t border-[#E2DFD8] pt-12 grid grid-cols-1 sm:grid-cols-2 gap-8">
            {t.landing.points.map((text, i) => {
              const Icon = ICONS[i];
              return (
                <div key={i} className="flex items-start gap-4">
                  <Icon className="w-5 h-5 text-[#A68A56] mt-0.5 shrink-0" strokeWidth={1.5} />
                  <span className="text-base text-[#2C363F]">{text}</span>
                </div>
              );
            })}
          </div>
          <div className="mt-16 max-w-2xl">
            <p className="font-display text-xl md:text-2xl font-light text-[#0B0E14] leading-relaxed">
              {t.landing.promise.map((line, i) => (
                <span key={i}>
                  {line}
                  <br />
                </span>
              ))}
            </p>
          </div>
        </section>
      </main>

      <footer className="border-t border-[#E2DFD8]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 py-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <Wordmark />
          <div className="flex flex-col sm:items-end gap-2">
            <p className="text-xs text-[#718096] max-w-md sm:text-right" data-testid="privacy-message">
              {t.privacy}
            </p>
            <div className="flex items-center gap-5 text-xs">
              <Link to="/privacy" data-testid="footer-privacy-link" className="text-[#4A5568] hover:text-[#0B0E14] underline underline-offset-2 transition-colors">{t.footer.privacy}</Link>
              <Link to="/terms" data-testid="footer-terms-link" className="text-[#4A5568] hover:text-[#0B0E14] underline underline-offset-2 transition-colors">{t.footer.terms}</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
